import api from './api';
import { captureSession, assertCurrentSession } from '../utils/authSession';
import { shippingAccessScope } from '../hooks/useShippingRequest';

const datasets = [
  'customers',
  'orders',
  'ivr',
  'shipments',
  'purchases',
  'bank',
  'cash_bank',
  'support',
  'resources',
];
const validId = (id) => typeof id === 'string' && /^[a-f\d]{24}$/.test(id);
const unavailable = () => {
  throw new Error('Distributor report unavailable.');
};

async function distributorReport(params, input = {}, binary = false) {
  const sessionBinding = input.sessionBinding || captureSession();
  const scope = shippingAccessScope();
  let user;
  try {
    user = JSON.parse(localStorage.getItem('user'));
  } catch {
    unavailable();
  }
  if (!sessionBinding.userId || !sessionBinding.hadToken || !datasets.includes(params.dataset))
    unavailable();
  if (user?.role === 'admin') {
    if (!validId(params.distributorId)) unavailable();
  } else if (user?.role !== 'master_distributor' || params.distributorId !== undefined)
    unavailable();
  assertCurrentSession(sessionBinding);
  const body = { ...params };
  if (binary) {
    delete body.page;
    delete body.limit;
  }
  const response = await api.post(`/reports/distributor/${binary ? 'export' : 'search'}`, body, {
    signal: input.signal,
    sessionBinding,
    skipAuthReplay: true,
    timeout: binary ? 120000 : 30000,
    ...(binary ? { responseType: 'blob' } : {}),
  });
  assertCurrentSession(sessionBinding);
  if (input.signal?.aborted || scope !== shippingAccessScope()) unavailable();
  if (binary) {
    const mime = response?.headers?.['content-type'] || '';
    if (!mime.includes('text/csv') && !mime.includes('spreadsheetml')) unavailable();
    return response;
  }
  const data = response?.data?.data;
  const distributorId = params.distributorId || user?.distributor?._id || user?.distributor;
  if (
    !data ||
    data.dataset !== params.dataset ||
    !validId(data.distributorId) ||
    (distributorId && data.distributorId !== distributorId) ||
    !Array.isArray(data.columns) ||
    !data.columns.length ||
    data.columns.some(
      (column) => typeof column.key !== 'string' || typeof column.label !== 'string'
    ) ||
    !Array.isArray(data.rows) ||
    data.rows.some((row) => !row || typeof row !== 'object') ||
    !Number.isSafeInteger(data.total) ||
    data.total < 0 ||
    data.page !== (params.page || 1) ||
    data.limit !== (params.limit || 25) ||
    data.rows.length > data.limit ||
    data.rows.length > data.total ||
    typeof data.history !== 'string' ||
    typeof data.dateField !== 'string' ||
    !Array.isArray(data.excludedFields) ||
    data.excludedFields.some((value) => typeof value !== 'string') ||
    !Array.isArray(data.statuses) ||
    data.statuses.some((value) => typeof value !== 'string')
  )
    unavailable();
  return data;
}

/**
 * Turn an export response into a file the browser saves.
 *
 * The type and extension come from the SERVER's Content-Type rather than being hardcoded. The
 * Order Tracker ships as a formatted .xlsx while the other reports are still CSV, and a workbook
 * saved as "report.csv" opens as garbage - Excel trusts the extension, not the bytes. Deriving
 * both from the response means adding or changing a format server-side needs no matching edit
 * here, which is the failure this is guarding against: the same bug was fixed on the Orders page
 * by hardcoding .xlsx there, and the Reports page kept saving the identical workbook as .csv.
 */
export function saveExportResponse(res, baseName) {
  const contentType = (res.headers?.['content-type'] || '').split(';')[0].trim();
  const isXlsx = contentType.includes('spreadsheetml') || contentType.includes('excel');
  const ext = isXlsx ? 'xlsx' : 'csv';
  const type = isXlsx
    ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    : 'text/csv';

  const url = window.URL.createObjectURL(new Blob([res.data], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${baseName}.${ext}`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

const reportService = {
  searchDistributor: (params, input) => distributorReport(params, input),
  exportDistributor: (params, input) => distributorReport(params, input, true),
  getOverview: () => api.get('/admin/reports/overview'),
  exportData: (type, params = {}) =>
    api.get('/admin/reports/export', {
      params: { type, ...params },
      responseType: 'blob',
    }),
  /** Tenant-scoped export. Private search terms stay in the body, like the order list. */
  exportMyOrders: (params = {}) =>
    api.post('/reports/orders-export', params, {
      responseType: 'blob',
    }),
};

export default reportService;
